<template>
    <div v-show="visible" class="fixed inset-0 z-[80] bg-transparent-basic-3 flex items-center justify-center">
        <div class="rounded-4 shadow-card bg-white w-[480px] max-h-[90vh] relative overflow-hidden">
            <div class="p-4 pb-3 flex justify-between items-center border-b-1 border-border-basic-2">
                <h2 class="font-heading-sm-semibold">
                    {{ title }}
                </h2>
                <a href="javascript:void(0)" class="ml-20" @click="closeConfirm" aria-label="Close">
                    <img src="@/assets/img/app-popup-1.svg" alt="app-popup-1" />
                </a>
                <!-- <button type="button" @click="closeConfirm" aria-label="Close">
                    <CloseIcon :width="24" :height="24" color="text-content-basic-1" />
                </button> -->
            </div>

            <div
                v-if="enableItemManagement" id="add-new-item"
                class="px-6 py-4 border-b border-border-basic-2 flex justify-between items-center gap-3 shrink-0"
            >
                <DesignButton
                    btnType="PRIMARY" btnVariant="BORDER"
                    btnSize="SM"
                    class="w-full"
                    :btnDisabled="false"
                    :btnText="itemManagementButtonText"
                    @click-event="onClickAdd"
                />
            </div>

            <div class="w-full flex-1 flex flex-col overflow-hidden">
                <div class="px-6 py-3 bg-background-basic-2 flex items-center justify-between gap-2 shrink-0">
                    <div class="flex items-center gap-1">
                        <div class="font-body-sm-semiobold text-content-basic-2">Display order</div>
                        <div :ref="attachInfoTooltip">
                            <InfoIcon :width="20" :height="20" color="text-gray-600" weight="REGULAR" />
                        </div>
                    </div>
                </div>
                
                <div ref="reorderScrollContainer" class="flex-1 overflow-y-auto h-auto max-h-[50vh] overflow-scroll box-border">
                    <draggable
                        v-model="localItems"
                        :item-key="itemKey"
                        handle=".handle"
                    >
                        <template #item="{ element, index }">
                            <div class="flex items-center gap-3 p-4 border-t-1 border-border-basic-1">
                                <DragHandleIcon class="handle cursor-move" color="text-content-basic-1" :width="24" :height="24" />
                                <div class="font-body-md-medium flex-1 ellipsis-2 break-words">{{ getLabel(element) }}</div>
                                <designDropdown 
                                    v-if="enableItemManagement" class="self-center flex-shrink-0"
                                    :menuItems="[
                                        { label : 'Edit', visible : true, action : ()=>{ (onClickEdit(element)); } }, 
                                        { label : 'Delete', visible : true, action : ()=>{ (onClickDelete(element)); } }, 
                                    ]"
                                    :dropUp="shouldDropUp(index)"
                                    :dropRight="true"
                                    dropdownWidth="w-32"
                                >
                                    <template v-slot:dropdown-icon>
                                        <img :src="require(`@/assets/img/kebab-icon.svg`)"/>
                                    </template>
                                </designDropdown>
                            </div>
                        </template>
                    </draggable>
                </div>
            </div>
            <div class="sticky bottom-0 px-4 py-4 flex justify-end gap-2 shrink-0 border-t-1 border-border-basic-1 bg-white">
                <DesignButton class="w-20" btnType="QUATERNARY" btnSize="MD" :btnDisabled="false" btnText="Cancel" @click-event="onCancel" />
                <DesignButton class="w-20" btnType="QUATERNARY" btnSize="MD" :btnDisabled="false" btnText="Reset" @click-event="onReset" />
                <DesignButton class="w-20" btnType="PRIMARY" btnSize="MD" :btnDisabled="false" btnText="Save" @click-event="onSave" />
            </div>
        </div>
    </div>
    <ElementsTooltipSingleton :ref="setTooltipRef" />
    <Teleport to="body">
        <AppPopupTwoButton
            :hideTitle="true"
            buttonText="Leave Anyway"
            v-model="showLeaveConfirm"
            @buttonEvent="onLeave"
        >
            <div class="font-normal text-lg leading-normal tracking-[-0.01rem] text-center">
                Your changes haven't been saved. Please click the Save button to keep your updates.
            </div>
        </AppPopupTwoButton>
    </Teleport>
</template>

<script>
import DragHandleIcon from '@/components/design-system/icon/drag-handle.vue';
import CloseIcon from '@/components/design-system/icon/close.vue';
import InfoIcon from '@/components/design-system/icon/info.vue';
import ElementsTooltipSingleton from '@/components/elements/ElementsTooltipSingleton.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import draggable from 'vuedraggable';
import designDropdown from './design-dropdown.vue';
import AppPopupTwoButton from '../AppPopupTwoButton.vue';

export default {
    name: 'DesignReorderModal',
    components: { 
        DragHandleIcon, draggable, CloseIcon, InfoIcon, ElementsTooltipSingleton, 
        DesignButton, designDropdown, AppPopupTwoButton },
    props: {
        modelValue: { type: Boolean, default: false },
        items: { type: Array, default: () => [] },
        itemKey: { type: String, default: 'id' },
        labelKey: { type: String, default: 'name' },
        orderKey: { type: String, default: 'order' },
        title: { type: String, default: 'Reorder' },
        tooltip: { type: String, default: 'Please drag and drop to reorder items.' },
        // add/edit/delete 같은 아이템 관리 액션을 사용할지 여부
        enableItemManagement : { type : Boolean, default: false },
        itemManagementButtonText: { type: String, default: '+ Add' },
    },
    emits: ['update:modelValue', 'save', 'add-click', 'edit-click', 'delete-click'],
    data() {
        return {
            localItems: [],
            initialItems: [],
            tooltipApi: null,
            showLeaveConfirm : false,
        };
    },
    computed: {
        visible: {
            get() { return this.modelValue; },
            set(v) { this.$emit('update:modelValue', v); },
        },
    },
    mounted() {
        if (this.modelValue) {
            this.onOpen();
        }
    },
    watch: {
        items: {
            immediate: true,
            handler(newVal) {
                const copy = Array.isArray(newVal) ? JSON.parse(JSON.stringify(newVal)) : [];
                this.localItems = copy;
            },
        },
        modelValue(newVal) {
            if (newVal) {
                // 모달이 열릴 때 초기값 세팅
                this.onOpen();
                return;
            }

            if (!newVal) {
                // 모달이 닫힐 때 변경 중이던 순서를 초기 상태로 되돌림
                this.onReset();
                return;
            }
        },
    },
    methods: {
        getLabel(el) {
            return el?.[this.labelKey] ?? '';
        },
        setTooltipRef(el) {
            this.tooltipApi = el;
        },
        attachInfoTooltip(el) {
            if (!el || !this.tooltipApi || !this.tooltipApi.loadBasic) return;
            this.tooltipApi.loadBasic(el, () => this.tooltip, { placement: 'top' });
        },
        closeConfirm(){
            if (this.hasUnsavedChanges()) {
                this.showLeaveConfirm = true;
                return;
            }
            this.onCancel();
        },
        onLeave(){
            this.visible = false;
            this.showLeaveConfirm = false;
        },
        onCancel() {
            this.visible = false;
        },
        onOpen() {
            this.initialItems = JSON.parse(JSON.stringify(this.localItems));
            this.$nextTick(() => {
                const scrollContainer = this.$refs.reorderScrollContainer;
                if (scrollContainer) {
                    scrollContainer.scrollTop = 0;
                }
            });
        },
        onReset() {
            this.localItems = JSON.parse(JSON.stringify(this.initialItems));
        },
        onSave() {
            if (!this.hasUnsavedChanges()) {
                this.onCancel();
                return;
            }

            let result = this.localItems.map((item, idx) => ({
                ...item,
                seq: item?.[this.itemKey],
                [this.orderKey] : idx,
            }));
            this.$emit('save', result);
            this.onCancel();
        },
        onClickAdd() {
            this.$emit('add-click', { items: this.localItems });
        },
        onClickEdit(item) {
            this.$emit('edit-click', { item: item, items: this.localItems });
        },
        onClickDelete(item) {
            this.$emit('delete-click', { item: item, items: this.localItems });
        },
        hasUnsavedChanges() {
            const current = JSON.stringify(this.localItems ?? []);
            const initial = JSON.stringify(this.initialItems ?? []);
            return current !== initial;
        },
        shouldDropUp(idx) {
            if (!Array.isArray(this.localItems) || this.localItems.length < 4) return false;
            const threshold = this.localItems.length - 2;
            return idx >= threshold;
        },
    },
};
</script>
